<script setup lang="ts">
import {
  ref,
  reactive,
  onBeforeMount,
  onBeforeUnmount,
  defineAsyncComponent,
  onMounted,
  Ref,
} from "vue";
import { useAlertsStore } from "@/stores/alerts.ts";
import { useFormAlertsStore } from "@/stores/alerts.ts";
// @ts-ignore
import i18n from "@/localization/i18n";
import { signInAsPOSPartner } from "@/services";
import {
  createPOSUserAuthSession,
  getPOSUserFromLocalStorage,
} from "@/utils/functions.ts";
import constants from "@/utils/constants.ts";
// import LanguageChanger from '@/views/components/LanguageChanger.vue';
import useAuthStore from "@/stores/auth.ts";
import { useUserStore } from "@/stores/user.ts";
import schema from "@/validation/partnerLoginSchema.ts";
import CustomInput from "@/views/components/CustomInput.vue";
import CustomChecker from "@/views/components/CustomChecker.vue";

import MessagesVue from "@/views/components/Messages.vue";
import useNavigationStore from "@/stores/ui/navigation.ts";
import Link from "@/views/components/Link.vue";
import { navigate, reload } from "vike/client/router";
import { usePageContext } from "vike-vue/usePageContext";
import TopelaDesktopLogo from "@/views/components/TopelaDesktopLogo.vue";
// @ts-ignore
const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL;

const t = i18n.global.t;
const isMobile: Ref<boolean> = ref(false);
const mobileMaxWIdth = 1300;
const alerts = useAlertsStore();
const loginAlerts = useFormAlertsStore();
const auth = useAuthStore();
const userStore = useUserStore();
const navigationStore = useNavigationStore();
const pageContext = usePageContext();

const loading = ref(false);
const rememberMe = ref(true);

const turnstile_site_key = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const formData = reactive({
  email: "",
  password: "",
  rememberMe: true,
});

const formErrors = reactive({
  email: "",
  password: "",
  rememberMe: "",
});

const NotificationToast = defineAsyncComponent(
  () => import("@/views/components/NotificationToast.vue"),
);

const handleResize = () => {
  isMobile.value = window.innerWidth <= mobileMaxWIdth;
};

const loginUser = () => {
  alerts.clearAlerts();
  loginAlerts.clearAlerts();
  formErrors.email = "";
  formErrors.password = "";

  formData.email = formData.email.trim();
  formData.password = formData.password.trim();

  schema
    .validate(formData, { abortEarly: false })
    .then(() => {
      loading.value = true;
      signInAsPOSPartner({
        username: formData.email.trim(),
        password: formData.password.trim(),
        keep_connected: formData.rememberMe,
      })
        .then((response) => {
          console.log("response.data", response.data)
          //this bloc verifies that the user trying to login is no blocked
          if (response.status === 200 && response.data.is_active === false) {
            loading.value = false;
            loginAlerts.putAlert({
              code: constants.LOGIN_FAILED_MESSAGE,
              scope: "login",
            });
            return;
          }

          createPOSUserAuthSession(response.data, rememberMe.value)
            .then(async () => {
                const user = getPOSUserFromLocalStorage()
                userStore.addPOSUser(user)

                loading.value = false;
                localStorage.setItem("showLoginSuccessAlert", "yes");
                if (!pageContext.urlOriginal.startsWith("/partner/login")) {
                    await reload();
                } else if (pageContext.urlParsed.search.destination) {
                    await navigate(pageContext.urlParsed.search.destination);
                } else if ((user as any)?.role === 'g_lst_role_request_advisor') {
                    await navigate("/partner/requests/");
                } else {
                    await navigate("/partner/home");
                }
              });

          console.log("response.data", response.data);
        })
        .catch(async (err) => {
          loading.value = false;
          formData.password = "";

          if (err.code === "ERR_NETWORK") {
            await navigate("/oups");
          } else if (err.response.status === constants.STATUS_LOGIN_FAILLED) {
            loginAlerts.putAlert({
              code: constants.LOGIN_FAILED_MESSAGE,
              scope: "login",
            });
          } else if (err.response.status === constants.STATUS_EXCEEDED_LIMIT) {
            loginAlerts.putAlert({
              code: constants.LOGIN_ATTEMPT_LIMIT_MESSAGE,
              scope: "login",
            });
          } else if (err.response.status === constants.STATUS_LOGIN_500) {
            await navigate("/oups");
          }
        });
    })
    .catch((error) => {
      console.log(error);
      error.inner.forEach((e: any) => {
        // @ts-ignore
        formErrors[e.path] = e.message;
      });
    });
};

onBeforeMount(() => {
  loginAlerts.clearAlerts();
});

onMounted(() => {
  document
    .querySelector('link[rel="canonical"]')
    ?.setAttribute("href", document.location.href);
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  loginAlerts.clearAlerts();
});

const scrollToSection = async (id: string) => {
  navigationStore.setHeaderScrollTo(id);
  navigationStore.setHomeScrollTo(id);
  await navigate("/");
};
</script>

<template>
  <div>
    <nav
      id="header-public"
      class="navbar navbar-expand-md bg-inverse position-fixed top-0 start-0 scrolling-navbar w-100 py-2"
      style="z-index: 9999; background-color: #f6f6f6"
    >
      <div class="container" style="max-width: 1000px">
        <Link href="/" class="navbar-brand mx-0 d-none d-lg-block">
          <TopelaDesktopLogo :classes="['me-1']" />
        </Link>
        <Link href="/" class="navbar-brand mx-0 d-block d-lg-none">
          <img
            :src="VITE_ASSET_URL + 'assets/logo/logos_Topela_Phone.svg'"
            width="50px"
            alt="Logo topela"
          />
        </Link>
        <ul class="navbar-nav mr-auto justify-content-end align-items-center">
          <li
            class="nav-item d-inline d-sm-inline d-md-inline d-lg-inline d-xl-inline"
          >
            <a
              class="btn btn-dark btn-text-visibility rounded-3 sized-text"
              href="/contact/"
            >
              {{ t('w_txt_footer_contact_heading') }}
            </a>
          </li>
        </ul>
      </div>
    </nav>
  </div>
  <template v-if="alerts.getSize > 0">
    <NotificationToast />
  </template>
  <div
    id="main-container"
    class="horizontal-layout horizontal-menu blank-page navbar-floating footer-static"
    data-open="hover"
    data-menu="horizontal-menu"
    data-col="blank-page"
  >
    <!-- BEGIN: Content-->
    <div class="app-content content">
      <div class="content-overlay"></div>
      <div class="header-navbar-shadow"></div>
      <div class="content-wrapper">
        <div class="content-header row"></div>
        <div class="content-body pb-5">
          <div class="auth-wrapper auth-cover">
            <div class="auth-inner row m-0">
              <!-- Register-->

              <div
                class="container h-100 d-flex align-items-center justify-content-center mt-5 px-0 px-md-2"
              >
                <div class="card signup-card" style="max-width: 500px">
                  <form
                    id="loginForm"
                    class="row gy-1"
                    @submit.prevent="loginUser"
                  >
                    <div class="">
                      <h2 class="" style="font-weight: bold">
                        {{ t('w_txt_partner_login_heading') }}
                      </h2>
                    </div>

                    <template v-if="loginAlerts.getSize > 0">
                      <template v-for="alert in loginAlerts.getAlerts">
                        <MessagesVue v-if="alert.scope === 'login'" />
                      </template>
                    </template>

                    <div class="col-12">
                      <CustomInput
                        type="text"
                        id="email"
                        autocomplete="email"
                        v-model:value="formData.email"
                        :errors="formErrors.email"
                        :placeholder="t('g_frm_email')"
                      />
                    </div>
                    <div class="col-12 mt-2">
                      <CustomInput
                        type="password"
                        id="login_password"
                        autocomplete="current-password"
                        v-model:value="formData.password"
                        :errors="formErrors.password"
                        :placeholder="t('g_frm_password')"
                      />
                    </div>
                    <!-- <div class="col-12 mt-1 mb-2 text-center">
                                             <div class="captcha-wrapper"
                                            :style="{
                                            transform: isMobile ? 'scale(1.17)' : 'scale(1.38)',
                                            transformOrigin: 'top center',
                                            marginTop: '10px',
                                            marginBottom: '10px'
                                        }">
                                            <vue-turnstile :site-key="turnstile_site_key"
                                                v-model="formData.cloud_flare_token" />
                                                
                                                </div>
                                            <p class="text-danger mt-0 mt-md-2" v-if="formErrors.cloud_flare_token">{{
                                                formErrors.cloud_flare_token }}</p>
                                        </div> -->
                    <div class="col-12">
                      <div class="row">
                        <div class="d-flex flex-row align-items-center mt-2">
                          <div
                            class="form-check form-switch form-check-primary"
                            style="
                              min-width: auto;
                              padding-left: 0;
                              margin-right: 15px;
                            "
                          >
                            <CustomChecker
                              id="customSwitch11"
                              v-model:value="formData.rememberMe"
                              :user-sized-class="true"
                              style="margin-left: 0"
                            >
                            </CustomChecker>
                          </div>
                          <label
                            class="form-check-label sized-text"
                            for="customSwitch11"
                            style=""
                          >
                            {{ t('m_act_signin_kepC') }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- <div class="col-12">
                                            <div class="d-flex flex-row align-items-center ">
                                                <div class="form-check form-switch form-check-primary"
                                                    style="min-width: auto; padding-left: 0; margin-right: 15px;">
                                                    <CustomChecker :input-label="''" id="customSwitch12"
                                                        v-model:value="formData.terms" style="margin-left: 0;" />
                                                </div>
                                                <label class="form-check-label sized-text" for="customSwitch12"
                                                    style="">
                                                    {{ t('w_frm_signup_confirmReading') }}
                                                    <Link href="/general-conditions-of-use/" class="signUpLink text-decoration-underline"
                                                        target="_blank">{{ t('w_frm_signup_cgu') }}
                                                    </Link>
                                                </label>
                                            </div>
                                            <p class="mb-0 text-danger" v-if="formErrors.terms">{{ formErrors.terms }}
                                            </p>
                                        </div> -->
                    <div class="col-12 text-center pt-2">
                      <button
                        type="submit"
                        class="btn btn-warning me-1 text-normal pt-1 pb-1 sized-text rounded-2 py-2 px-5 w-100 mb-3"
                      >
                        <span v-if="!loading">{{ t("w_txt_signIn") }}</span>
                        <div
                          v-else
                          class="spinner-border spinner-border-sm text-light"
                          role="status"
                        >
                          <span class="sr-only">Loading...</span>
                        </div>
                      </button>
                      <!-- <a href="/partner/home/"
                                                class="btn btn-warning me-1 text-normal pt-1 pb-1 sized-text rounded-2 py-2 px-5 w-100 mb-3">
                                                <span v-if="!loading">{{ t('w_txt_signIn') }}</span>
                                                <div v-else class="spinner-border spinner-border-sm text-light"
                                                    role="status">
                                                    <span class="sr-only">Loading...</span>
                                                </div>
                                            </a> -->
                      <div class="text-start">
                        <div>
                          <!-- <p class="sized-text mb-1">
                                                        <Link href="/auth/password_reset/"
                                                            class="signUpLink text-decoration-underline text-secondary">
                                                            {{ t('w_txt_login_forgotPassword') }}
                                                        </Link>
                                                    </p> -->
                          <p class="sized-text mb-1">
                            {{ t('w_txt_partner_login_missing_account') }}
                            <Link
                              href="/contact/"
                              class="signUpLink text-decoration-underline"
                              id="registerLink"
                            >
                              {{ t('w_txt_partner_login_contact_us') }}
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <!-- /Register-->
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- END: Content-->
  </div>
</template>

<style scoped>
@import url("@/assets/css/bootstrap-extended2.css");
@import url("@/assets/css/bootstrap-extended.css");
@import url("@/assets/css/animate.css");
@import url("@/assets/css/main.css");
@import url("@/assets/css/responsive.css");

#leftCover {
  background-size: cover;
}

/* .form-check-input:checked {
        background-color: #FEB23C;
        border-color: #FEB23C;
    } */

.form-check-input {
  width: 3rem;
}

.form-check-input:hover {
  cursor: pointer;
}

/* .form-control:focus {
        border-color: #FEB23C;
        box-shadow: 0 0 0 0.02rem #FEB23C;
        padding-left: 1.5rem;
    }
    .signUpLink {
        color: #FEB23C !important;
        text-decoration: none !important;
    } */

.custom-btn {
  color: white !important;
  background-color: #233648;
}

.custom-btn:hover {
  background-color: white;
  color: #233648 !important;
  border: #233648 1px solid;
}
</style>
